import graphql from "babel-plugin-relay/macro";
import { useMemo } from "react";

import { InputForUpdateEventSignUp } from "../../EventDetail/hooks/__generated__/useUpdateEventSignUpMutation.graphql";
import type { EventSignupSummaryPaymentSummary_signUp$data } from "../components/__generated__/EventSignupSummaryPaymentSummary_signUp.graphql";
import type { EventSignupSummaryReservation_signUp$data } from "../components/__generated__/EventSignupSummaryReservation_signUp.graphql";
import type { useBuildEventSignUpUpdateInput_signUp$data } from "./__generated__/useBuildEventSignUpUpdateInput_signUp.graphql";

/** Fragment must spread `...useBuildEventSignUpUpdateInput_signUp @relay(mask: false)`. */
export const UseBuildEventSignUpUpdateInputFragment = graphql`
  fragment useBuildEventSignUpUpdateInput_signUp on EventSignUp {
    event {
      id
      primaryChargeType
    }
    registrations(first: 50) {
      edges {
        node {
          id
          type
          index
          ... on MemberEventRegistration {
            payingMember {
              id
            }
          }
          ... on GuestEventRegistration {
            payingMember {
              id
            }
          }
          ... on CustomerEventRegistration {
            payingCustomer {
              id
            }
          }
          primaryChargeType {
            id
            chargeRuleSet {
              chargeRules(first: 1) {
                edges {
                  node {
                    price
                  }
                }
              }
            }
          }
          registrant {
            id
          }
          formResponses(first: 50) {
            edges {
              node {
                id
                booleanValue
                stringValue
                transportationType {
                  id
                }
                questionConfiguration {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;

type RegistrationForUpdate = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        useBuildEventSignUpUpdateInput_signUp$data["registrations"]
      >["edges"]
    >[number]
  >["node"]
>;

/**
 * `EventSignUp` data from a parent `useFragment` that spreads
 * `...useBuildEventSignUpUpdateInput_signUp @relay(mask: false)` (Relay does not
 * expose a second fragment ref for unmasked sub-selections).
 */
export type EventSignUpDataForUpdateInput =
  | EventSignupSummaryPaymentSummary_signUp$data
  | EventSignupSummaryReservation_signUp$data;

const buildFormResponses = (registration: RegistrationForUpdate) =>
  registration.formResponses?.edges?.map((formResponse) => ({
    booleanValue: formResponse?.node?.booleanValue,
    questionConfigurationId: formResponse?.node?.questionConfiguration.id ?? "",
    stringValue: formResponse?.node?.stringValue,
    transportationTypeId: formResponse?.node?.transportationType?.id,
  }));

const buildEventSignUpUpdateInputFromData = (
  signUp: Pick<useBuildEventSignUpUpdateInput_signUp$data, "registrations">
): InputForUpdateEventSignUp => {
  const registrations =
    signUp.registrations?.edges?.flatMap((edge) =>
      edge?.node ? [edge.node] : []
    ) ?? [];

  return {
    memberRegistrations: registrations.flatMap((registration, index) =>
      registration.type === "MEMBER"
        ? [
            {
              id: registration.id,
              index: registration.index ?? index,
              payingMemberId:
                registration.payingMember?.id ??
                registration.registrant?.id ??
                "",
              playerId: registration.registrant?.id ?? null,
              formResponses: buildFormResponses(registration),
              primaryRegistrationChargeTypeId:
                registration.primaryChargeType?.id ?? null,
            },
          ]
        : []
    ),
    customerRegistrations: registrations.flatMap((registration, index) =>
      registration.type === "CUSTOMER"
        ? [
            {
              id: registration.id,
              index: registration.index ?? index,
              payingCustomerId:
                registration.payingCustomer?.id ??
                registration.registrant?.id ??
                null,
              playerId: registration.registrant?.id ?? null,
              formResponses: buildFormResponses(registration),
              primaryRegistrationChargeTypeId:
                registration.primaryChargeType?.id ?? null,
            },
          ]
        : []
    ),
    guestRegistrations: registrations.flatMap((registration, index) =>
      registration.type === "GUEST"
        ? [
            {
              id: registration.id,
              index: registration.index ?? index,
              payingMemberId:
                registration.payingMember?.id ??
                registration.registrant?.id ??
                "",
              playerId: registration.registrant?.id ?? null,
              formResponses: buildFormResponses(registration),
              primaryRegistrationChargeTypeId:
                registration.primaryChargeType?.id ?? null,
            },
          ]
        : []
    ),
  };
};

const registrationSubtotalFromData = (
  signUp: Pick<
    useBuildEventSignUpUpdateInput_signUp$data,
    "event" | "registrations"
  >
): number => {
  if (signUp.event?.primaryChargeType !== "PER_SIGN_UP") {
    return (
      signUp.registrations?.edges?.reduce(
        (sum, edge) =>
          sum +
          (edge?.node?.primaryChargeType?.chargeRuleSet?.chargeRules?.edges?.[0]
            ?.node?.price ?? 0),
        0
      ) ?? 0
    );
  }

  const seenChargeTypeIds = new Set<string>();

  return (
    signUp.registrations?.edges?.reduce((sum, edge) => {
      const registration = edge?.node;
      const price =
        registration?.primaryChargeType?.chargeRuleSet?.chargeRules?.edges?.[0]
          ?.node?.price ?? 0;
      const chargeTypeId = registration?.primaryChargeType?.id;

      if (!chargeTypeId) {
        return sum + price;
      }

      if (seenChargeTypeIds.has(chargeTypeId)) {
        return sum;
      }

      seenChargeTypeIds.add(chargeTypeId);
      return sum + price;
    }, 0) ?? 0
  );
};

/**
 * Derives the update payload and registration price subtotal from parent
 * `useFragment` data that includes `useBuildEventSignUpUpdateInput_signUp`
 * (unmasked).
 */
export const useBuildEventSignUpUpdateInput = (
  signUp: EventSignUpDataForUpdateInput
): {
  eventSignUpInput: InputForUpdateEventSignUp;
  registrationSubtotal: number;
} =>
  useMemo(
    () => ({
      eventSignUpInput: buildEventSignUpUpdateInputFromData(signUp),
      registrationSubtotal: registrationSubtotalFromData(signUp),
    }),
    [signUp]
  );
